import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Slider from '@mui/material/Slider'
import PlayArrow from '@mui/icons-material/PlayArrow'
import PauseCircle from '@mui/icons-material/PauseCircle'
import format from 'date-fns/format'

import { CommonButton } from '../../../../../shared/components/common-button'
import {
  HeatmapResponse,
  useFetchCurrentCropTypesQuery,
  useLazyFetchWeeklyHeatmapQuery
} from '../../../../../store/api/api'
import { heatmapIdSelector, heatmapRequestParamsSelector } from '../../../../../store/heatmap/selectors'
import { setHeatmapDataAction } from '../../../../../store/heatmap/reducer'
import { transformHeatmapParams } from '../../transform-heatmap-params'
import { useMediaHook } from '../../../../../shared/hooks/useMediaHook'
import { PopoverWrapper } from '../../../../../shared/components/popover-wrapper/popover-wrapper'

import { useStyles } from './style'

function displayDate(dateStr?: string) {
  if (!dateStr) {
    return null
  }
  return format(new Date(dateStr), 'MMM do')
}

export const PlayWeeklyMap: React.FC = () => {
  const [heatmaps, setHeatmaps] = useState<HeatmapResponse[]>([])
  const [currentHeatmapIndex, setCurrentHeatmapIndex] = useState(0)
  const [loading, setLoading] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [fetchWeeklyHeatmapQuery] = useLazyFetchWeeklyHeatmapQuery()
  const { currentData: cropTypes } = useFetchCurrentCropTypesQuery()

  const mainHeatmapId = useSelector(heatmapIdSelector)
  const requestParams = useSelector(heatmapRequestParamsSelector)

  const mainHeatmapIdRef = useRef<number | null>(mainHeatmapId)
  const dispatch = useDispatch()

  const classes = useStyles()
  const { isMobile } = useMediaHook()

  useEffect(() => {
    if (requestParams && mainHeatmapId && heatmaps.length === 0) {
      setLoading(true)
      fetchWeeklyHeatmapQuery(transformHeatmapParams(requestParams, cropTypes))
        .unwrap()
        .then((data) => {
          setHeatmaps(data)
          setCurrentHeatmapIndex(data.length - 1)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [mainHeatmapId, fetchWeeklyHeatmapQuery, heatmaps, cropTypes, setHeatmaps, setLoading, setCurrentHeatmapIndex])

  useEffect(() => {
    if (isPlaying && heatmaps.length > 0) {
      const interval = setInterval(() => {
        setCurrentHeatmapIndex((index) => {
          if (index + 1 >= heatmaps.length) {
            return 0
          } else {
            return index + 1
          }
        })
      }, 2000)

      return () => clearInterval(interval)
    }
  }, [isPlaying, heatmaps])

  useEffect(() => {
    if (heatmaps[currentHeatmapIndex]) {
      dispatch(setHeatmapDataAction(heatmaps[currentHeatmapIndex]))
    }
  }, [currentHeatmapIndex, heatmaps, dispatch])

  // clear out the local heatmaps state when the main heatmap changes from filters
  useEffect(() => {
    if (mainHeatmapId) {
      if (mainHeatmapIdRef.current && mainHeatmapIdRef.current !== mainHeatmapId) {
        setHeatmaps([])
        setIsPlaying(false)
      }
      mainHeatmapIdRef.current = mainHeatmapId
    }
  }, [setHeatmaps, setIsPlaying, mainHeatmapId, mainHeatmapIdRef.current])

  const disabledControls = loading || heatmaps.length === 0

  return (
    <div className={classes.container}>
      <PopoverWrapper step={9} media={['desktop', 'mobile']}>
        <CommonButton
          disabled={disabledControls}
          onClick={() => {
            setIsPlaying((playing) => !playing)
          }}
          ref={React.createRef()}
        >
          {isPlaying && !loading ? <PauseCircle sx={{ fontSize: 32 }} /> : <PlayArrow sx={{ fontSize: 32 }} />}
        </CommonButton>
      </PopoverWrapper>
      {!isMobile ? (
        <div className={classes.sliderContainer}>
          <Slider
            aria-label='Viewing Date'
            onChange={(_ev, value) => {
              setCurrentHeatmapIndex(value as number)
            }}
            disabled={disabledControls}
            min={0}
            max={heatmaps.length - 1}
            getAriaValueText={(value) => {
              return heatmaps[value]?.date
            }}
            value={currentHeatmapIndex}
          />
          <p className={classes.sliderDate}>
            {heatmaps.length > 0 ? displayDate(heatmaps[currentHeatmapIndex]?.date) : null}
          </p>
        </div>
      ) : null}
    </div>
  )
}
